/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Dropdown,
  TextInput,
  PasswordInput,
  Loading,
  InlineNotification
} from 'carbon-components-react';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import SwaggerUI from 'swagger-ui-react';

import { isFeatureEnabled } from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FeatureFlag } from '../../../../../ts/feature-flags';
import { selectTargetEnvironmentId } from '../../../../ia/store/environments/selectors';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { selectCatalogItemById } from '../../../store/catalog/selectors';
import {
  selectDraftIntegration,
  selectIntegrationById
} from '../../../store/channels/selectors';
import { ChannelContentProps } from '../../../util/types';
import { AUTHENTICATION_METHODS, CODE, STATE, VALUE } from '../constants';
import {
  AuthenticationMethods,
  getAuthenticationMethods,
  getAuthFriendlyName
} from '../helpers';
import { FIELDS } from '../schema';
import Servers from '../swagger-ui-overrides/servers/servers';
import { AuthenticationContentOAuth } from './AuthenticationContentOAuth';
import { AuthenticationStepMessages as messages } from './messages';

export const AuthenticationContent = ({
  assistantId,
  catalogItemId,
  environmentId,
  isQuickSetup,
  readOnly
}: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue, registerInputField } =
    useFormHelpers(formMethods);

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const authCode = queryParams.get(CODE);
  const { authName = '' } = JSON.parse(queryParams.get(STATE) ?? '{}');

  const draftIntegration = useAppSelector(selectDraftIntegration, shallowEqual);
  const savedIntegration = useAppSelector(
    state => selectIntegrationById(state, draftIntegration?.integration_id),
    shallowEqual
  );
  const isUpdateExtensionEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.INTEGRATIONS_CUSTOM_UPDATE)
  );
  const liveEnvironmentId = useAppSelector(selectTargetEnvironmentId);
  const isLiveExtension = environmentId === liveEnvironmentId;

  // These are the states associated with the secret fields for the supported authentication methods
  const basicUsernameChangeState = useState<boolean>(false);
  const basicPasswordChangeState = useState<boolean>(false);
  const bearerTokenChangeState = useState<boolean>(false);
  const apiKeyChangeState = useState<boolean>(false);

  // This checks if the provided state is changed based on the boolean value
  const isStateChange = state => state[0];

  const catalogItem = useAppSelector(state =>
    selectCatalogItemById(state, catalogItemId)
  );

  const authenticationMethods: AuthenticationMethods[] =
    getAuthenticationMethods(
      catalogItem?.specification?.body?.components?.securitySchemes || {}
    ) || [{ authenticationName: 'none', auth: AUTHENTICATION_METHODS.NONE }];

  // In the case of OAuth - authCode flow, we save authenticationMethod as part of query param and
  // on successful redirect back along with code, we get back that method.
  const authenticationMethod = !_.isNil(authCode)
    ? AUTHENTICATION_METHODS.OAUTH2
    : getFieldValue(FIELDS.AUTHENTICATION_METHOD);

  // In the case of OAuth - authCode flow, we save authenticationName as part of query param and
  // on successful redirect back along with code, we get back that name.
  const authenticationName = !_.isNil(authCode)
    ? authName
    : getFieldValue(FIELDS.AUTHENTICATION_DISPLAY_NAME);

  // Extract the authentication method from savedIntegration
  const savedAuthMethod =
    savedIntegration?.settings?.authentication?.authentication_method;

  // Check if the savedAuthMethod is NOT included in authenticationMethods
  // In the case that Tanya updates her extension's CatalogOpenAPI document in
  // her draft extension, and the live extension's authentication and/or server
  // url has been removed, we must notify her that the previously configured
  // values are no longer available. Note: the previously configured values still
  // work and they have NOT been auto changed although they are not shown.
  const authRemoved = !authenticationMethods.some(
    method => method.auth === savedAuthMethod
  );

  const servers = catalogItem?.specification?.body?.servers;
  const serverUrlRemoved = _.isNil(
    servers.find(
      server => server.url === savedIntegration?.settings?.server_url
    )
  );
  const showAuthRemovedErrorInLive =
    isUpdateExtensionEnabled && isLiveExtension && authRemoved && !isQuickSetup;
  const showServerUrlRemovedErrorInLive =
    isUpdateExtensionEnabled &&
    isLiveExtension &&
    serverUrlRemoved &&
    !isQuickSetup;

  const dropdownItems = authenticationMethods.map(
    ({ authenticationName, auth }, index) => {
      let friendlyName: string;

      // In the case of auth name as oauth2 and auth type as oauth2, only show as `OAuth 2.0`
      // In the case of auth name as customOauth2Name, `show OAuth 2.0 customOauth2Name` in the dropdown
      if (
        authenticationName &&
        authenticationName.toLowerCase() !== auth.toLowerCase() &&
        auth === AUTHENTICATION_METHODS.OAUTH2
      ) {
        friendlyName = `${getAuthFriendlyName[auth]} ${authenticationName}`;
      } else {
        // For standard authentication methods
        friendlyName =
          getAuthFriendlyName[auth as keyof typeof getAuthFriendlyName] ||
          'Unknown authentication method';
      }

      return {
        text: friendlyName,
        value: auth,
        id: index + 1,
        name: authenticationName
      };
    }
  );

  const setServer = useCallback(
    ({ field, value }) => {
      let shouldClearServerVariables = false;

      // Check if the changed value is a server url AND if it is a new value.
      // If that is the case, we need to refresh the set server variables.
      if (
        field === FIELDS.SERVER_URL &&
        getFieldValue(FIELDS.SERVER_URL) !== value
      ) {
        shouldClearServerVariables = true;
      }

      updateFieldValue(field, value);

      // If a new server url is selected from the dropdown, setting new server variables
      if (shouldClearServerVariables) {
        const servers = _.cloneDeep(catalogItem?.specification?.body?.servers);
        const newServerVariables =
          servers?.find(obj => obj.url === value).variables || {};
        updateFieldValue(FIELDS.SERVER_VARIABLES, newServerVariables);
      }
    },
    [catalogItem?.specification?.body?.servers, getFieldValue, updateFieldValue]
  );

  const setServerDefaultVariableValue = useCallback(
    params => {
      const { variableName, value } = params;
      updateFieldValue(FIELDS.SERVER_VARIABLE_VALUE(variableName), value);
      const serverVariableType = getFieldValue(
        FIELDS.SERVER_VARIABLE_TYPE(variableName)
      );
      if (!serverVariableType || serverVariableType === '') {
        updateFieldValue(FIELDS.SERVER_VARIABLE_TYPE(variableName), 'value');
      }
    },
    [getFieldValue, updateFieldValue]
  );

  const CarbonPlugin = {
    components: {
      info: () => null,
      Servers: ({ servers, ...rest }: any) =>
        servers && getFieldValue(FIELDS.SERVER_URL) ? (
          <Servers
            readOnly={readOnly}
            serverUrl={
              !showServerUrlRemovedErrorInLive
                ? getFieldValue(FIELDS.SERVER_URL)
                : catalogItem?.specification?.body?.servers[0].url
            }
            setServerUrl={setServer}
            serverVariables={
              !showServerUrlRemovedErrorInLive
                ? getFieldValue(FIELDS.SERVER_VARIABLES)
                : catalogItem?.specification?.body?.servers[0].variables
            }
            getServerDefaultVariableValue={varName =>
              getFieldValue(FIELDS.SERVER_VARIABLE_VALUE(varName))
            }
            setServerDefaultVariableValue={setServerDefaultVariableValue}
            servers={servers}
            {...rest}
          />
        ) : (
          <Loading />
        ),
      operations: () => null,
      Models: () => null
    }
  };

  const openAPISpec = _.cloneDeep(catalogItem?.specification?.body);
  // Lets hide all the auth stuff. It might be better to do custom overrides to all the components in the swagger here,
  // but this is much faster for now.
  if (openAPISpec?.components?.securitySchemes) {
    openAPISpec.components.securitySchemes = undefined;
  }
  if (openAPISpec?.security?.length) {
    openAPISpec.security = [];
  }
  // Set openapi spec servers to match updated values in config
  if (openAPISpec?.servers) {
    openAPISpec.servers = catalogItem?.specification?.body?.servers;
  }

  // Currently Tooling only supports the writing of one api key
  const firstApiKeyProps = (() => {
    const spec = catalogItem?.specification?.body;
    const securitySchemes = spec?.components?.securitySchemes;
    if (securitySchemes) {
      const securitySchemesKeyArray = Object.keys(securitySchemes);
      for (let i = 0; i < securitySchemesKeyArray.length; i++) {
        const key = securitySchemesKeyArray[i];
        const schemeItem = securitySchemes[key] as any;

        if (schemeItem.type === AUTHENTICATION_METHODS.API_KEY_TYPE) {
          // API key's have a name property which we should use over key if it exists
          // If we don't return name, the api key auth has a random additional key
          // called ApiKeyAuth
          const nameProp = schemeItem?.name;
          const inProp = schemeItem?.in;
          return { name: nameProp || key, in: inProp };
        }
      }
    }
    return null;
  })();

  // Fixme when store will start supporting partial updates
  // This useEffect is for non quick setup (edit flow),
  // for secrets, tooling need to send {type: 'value'} to store along with actual value since the default schema
  // (check schema.tsx) is casted for quicksetup flows.
  // This will check authenticationMethod and add type: 'value' to the secret fields
  // This will be only triggered if the secret fields are dirtied that is when state change happens
  useEffect(() => {
    if (!isQuickSetup) {
      if (
        authenticationMethod === AUTHENTICATION_METHODS.BASIC &&
        isStateChange(basicPasswordChangeState)
      ) {
        updateFieldValue(FIELDS.BASIC.PASSWORD_TYPE, VALUE);
      } else if (
        authenticationMethod === AUTHENTICATION_METHODS.BEARER &&
        isStateChange(bearerTokenChangeState)
      ) {
        updateFieldValue(FIELDS.BEARER.TOKEN_TYPE, VALUE);
      } else if (
        authenticationMethod === AUTHENTICATION_METHODS.API_KEY &&
        firstApiKeyProps?.name &&
        isStateChange(apiKeyChangeState)
      ) {
        updateFieldValue(FIELDS.APIKEY.TYPE(firstApiKeyProps.name), VALUE);
        // For API key auth it also need 'in' field
        updateFieldValue(
          FIELDS.APIKEY.IN(firstApiKeyProps.name),
          firstApiKeyProps.in
        );
      }
    }
  }, [
    apiKeyChangeState,
    authenticationMethod,
    basicPasswordChangeState,
    basicUsernameChangeState,
    bearerTokenChangeState,
    firstApiKeyProps,
    isQuickSetup,
    updateFieldValue
  ]);

  return (
    <>
      <div className="TabContent__subtitle">
        <FormattedMessage {...messages.subtitle} />
      </div>
      <div className="TabContent__spacer8" />
      <div className="TabContent__field-section">
        {showAuthRemovedErrorInLive && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={t(messages.authRemovedTitle)}
            subtitle={t(messages.authRemovedSubtitle)}
          />
        )}
        {showServerUrlRemovedErrorInLive && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={t(messages.serverUrlRemovedTitle)}
            subtitle={t(messages.serverUrlRemovedSubtitle)}
          />
        )}
        {authenticationMethods && authenticationMethods.length !== 1 && (
          <div className="TabContent__field-container">
            <Dropdown
              id={t(messages.authenticationType)}
              label={getAuthFriendlyName[AUTHENTICATION_METHODS.NONE]}
              titleText={t(messages.authenticationType)}
              items={dropdownItems}
              selectedItem={dropdownItems.find(item =>
                authenticationName
                  ? item.name === authenticationName
                  : item.value === authenticationMethod
              )}
              itemToString={item => item.text}
              onChange={value => {
                updateFieldValue(
                  FIELDS.AUTHENTICATION_METHOD,
                  value.selectedItem.value
                );
                updateFieldValue(
                  FIELDS.AUTHENTICATION_DISPLAY_NAME,
                  value.selectedItem.name
                );
              }}
            />
          </div>
        )}
        {!showAuthRemovedErrorInLive && (
          <>
            {authenticationMethod === AUTHENTICATION_METHODS.BASIC && (
              <>
                <div className="TabContent__field-container">
                  <TextInput
                    {...registerInputField({
                      field: FIELDS.BASIC.USERNAME,
                      isQuickSetup,
                      labelText: messages.username,
                      placeholderText: messages.usernamePlaceholder,
                      changeState: basicUsernameChangeState,
                      readOnly
                    })}
                    id="catalog-openapi-basic-username"
                  />
                </div>
                <div className="TabContent__spacer16" />
                <div className="TabContent__field-container">
                  <PasswordInput
                    {...registerInputField({
                      field: FIELDS.BASIC.PASSWORD,
                      isPasswordField: true,
                      isQuickSetup,
                      labelText: messages.password,
                      placeholderText: messages.passwordPlaceholder,
                      changeState: basicPasswordChangeState,
                      readOnly
                    })}
                    id="catalog-openapi-basic-password"
                  />
                </div>
              </>
            )}
            {authenticationMethod === AUTHENTICATION_METHODS.BEARER && (
              <div className="TabContent__field-container">
                <PasswordInput
                  {...registerInputField({
                    field: FIELDS.BEARER.TOKEN,
                    isPasswordField: true,
                    isQuickSetup,
                    labelText: messages.token,
                    placeholderText: messages.tokenPlaceholder,
                    changeState: bearerTokenChangeState,
                    readOnly
                  })}
                  id="catalog-openapi-bearer-token"
                />
              </div>
            )}
            {authenticationMethod === AUTHENTICATION_METHODS.API_KEY &&
              firstApiKeyProps?.name && (
                <>
                  <div className="TabContent__field-container">
                    <PasswordInput
                      {...registerInputField({
                        field: FIELDS.APIKEY.VALUE(firstApiKeyProps?.name),
                        isPasswordField: true,
                        isQuickSetup,
                        labelText: messages.apiKey,
                        placeholderText: messages.apiKeyPlaceholder,
                        changeState: apiKeyChangeState,
                        readOnly
                      })}
                      id="catalog-openapi-api-key"
                    />
                  </div>
                </>
              )}
            {catalogItem &&
              authenticationMethod === AUTHENTICATION_METHODS.OAUTH2 && (
                <AuthenticationContentOAuth
                  assistantId={assistantId}
                  catalogItemId={catalogItemId}
                  environmentId={environmentId}
                  isQuickSetup={isQuickSetup}
                  readOnly={readOnly}
                />
              )}
          </>
        )}
        <div className="TabContent__spacer16" />
        <div className="TabContent__field-container">
          <SwaggerUI
            spec={openAPISpec}
            supportedSubmitMethods={[]}
            tryItOutEnabled={false}
            plugins={[CarbonPlugin]}></SwaggerUI>
        </div>
      </div>
      <div className="TabContent__spacer24" />
    </>
  );
};
